import { z } from 'zod/v3';
import { Entity, EntityWithName } from '../common/queryClient';
import { ISODate, zValidPeriod } from '../common/utils';
import {
  ChangedBySchema,
  UserRoleFormDataType,
  UserViewPermissionDto,
} from './schema';
import { UserViewPermissionSchema } from './schema/viewDto/UserViewSchema';

export interface ChangedByDto {
  id: number;
  username: string;
  firstName?: string | null;
  lastName?: string | null;
}

export interface UserDirectoryDto extends Entity {
  id: number;
  username: string;
  firstName: string | null;
  lastName: string | null;
  nationalIdNumber: string | null;
  roles: string | null;
  validFrom: ISODate | null;
  validTo: ISODate | null;
  validPeriod: [ISODate | null, ISODate | null];
  isAdmin: boolean;
  isSuperadmin: boolean;
}

export interface User extends Entity {
  username: string;
  firstName: string | null;
  lastName: string | null;
  nationalIdNumber: string | null;
  validFrom: string | null; // date
  validTo: string | null; // date
  lastLoginAt: string | null; // datetime
  roles: UserRoleFormDataType[];
  viewPermissions: UserViewPermissionDto[];
  isAdmin: boolean;
  isSuperadmin: boolean;
}

export const SessionUserSchema = z.object({
  id: z.number(),
  createdAt: z.string().nullable(),
  createdBy: ChangedBySchema.nullable(),
  modifiedAt: z.string().nullable(),
  modifiedBy: ChangedBySchema.nullable(),
  username: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  nationalIdNumber: z.string().nullable(),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  viewPermissions: z.array(UserViewPermissionSchema),
  roles: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      validPeriod: zValidPeriod(),
      permissions: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          code: z.string(),
          checkScope: z.boolean().optional(),
        }),
      ),
      accessGroups: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          dataObjectGroups: z
            .object({ id: z.number(), code: z.string() })
            .array(),
          dataObjectDescriptions: z
            .object({ id: z.number(), code: z.string() })
            .array(),
          dataObjectDescriptionVersions: z.object({ id: z.number() }).array(),
        }),
      ),
    }),
  ),
  isAdmin: z.boolean(),
  isSuperadmin: z.boolean(),
});

export type SessionUserType = z.infer<typeof SessionUserSchema>;

export interface Role extends EntityWithName {
  permissions: Permission[];
  accessGroups: AccessGroup[];
  validPeriod: [string | null, string | null];
}

export type Permission = EntityWithName;
export type AccessGroup = EntityWithName;

export enum PermissionCode {
  INPUT_DESC_MANAGE = 'INPUT_DESC_MANAGE',
  OUTPUT_DESC_MANAGE = 'OUTPUT_DESC_MANAGE',
  DATA_OBJECT_GROUP_MANAGE = 'DATA_OBJECT_GROUP_MANAGE',
  CLASSIFIER_MANAGE = 'CLASSIFIER_MANAGE',
  SUBJECT_LIST_LOAD = 'SUBJECT_LIST_LOAD',
  SUBJECT_SINGLE_LOAD = 'SUBJECT_SINGLE_LOAD',
  SUBJECT_MANAGE = 'SUBJECT_MANAGE',
  SAMPLE_MANAGE = 'SAMPLE_MANAGE',
  SAMPLE_SUBJECT_REMOVE = 'SAMPLE_SUBJECT_REMOVE',
  INPUT_DATA_MANAGE = 'INPUT_DATA_MANAGE',
  OUTPUT_DATA_MANAGE = 'OUTPUT_DATA_MANAGE',
}

export const UserPermissionSchema = z.object({
  userId: z.number(),
  permissionCode: z.string(),
  groupId: z.number().nullable(),
  groupCode: z.string().nullable(),
  descriptionId: z.number().nullable(),
  descriptionCode: z.string().nullable(),
  versionId: z.number().nullable(),
});
export type UserPermissionType = z.infer<typeof UserPermissionSchema>;
